// Calculate weighted demographic metrics from origins data
export const calculateWeightedMetrics = (originsData) => {
  if (!originsData || !originsData.origins) {
    return null;
  }

  const origins = originsData.origins;
  let totalDevices = 0;

  // First pass: calculate total devices
  origins.forEach(origin => {
    totalDevices += origin.total_occurences || 0;
  });

  if (totalDevices === 0) {
    return null;
  }

  // Initialize metrics
  const metrics = {
    totalOrigins: origins.length,
    totalDevices,
    profile: {
      ageProfiles: {
        'Young Pre-Parental': 0,
        'Pre School Families': 0,
        'School Families': 0,
        'Mature Families': 0,
        'Empty Nester': 0,
        'Retired': 0
      },
      incomeProfiles: {
        'Struggling': 0,
        'Getting by': 0,
        'Managing': 0,
        'Comfortable': 0,
        'Affluent': 0
      }
    },
    socialGrade: {
      'AB': 0,
      'C1': 0,
      'C2': 0,
      'DE': 0
    },
    tenure: {
      'Owned Outright': 0,
      'Mortgage/Shared': 0,
      'Social Rented': 0,
      'Private Rented': 0
    },
    employment: {
      'Full-Time': 0,
      'Part-Time': 0,
      'Self-Employed (with employees)': 0,
      'Self-Employed (solo)': 0,
      'Unemployed': 0,
      'Student': 0,
      'Retired': 0,
      'Home Maker': 0,
      'Long-Term Sick/Disabled': 0,
      'Other': 0
    },
    education: {
      'No Qualifications': 0,
      'Level 1': 0,
      'Level 2': 0,
      'Apprenticeship': 0,
      'Level 3': 0,
      'Level 4+': 0
    },
    imd: {
      '1': 0, '2': 0, '3': 0, '4': 0, '5': 0,
      '6': 0, '7': 0, '8': 0, '9': 0, '10': 0
    },
    spend: {
      totalSpend: {},
      spendPerPerson: {}
    }
  };

  // Second pass: accumulate weighted values
  let totalPopulation = 0;
  origins.forEach(origin => {
    const weight = origin.total_occurences / totalDevices;
    if (origin.population) {
      totalPopulation += origin.population;
    }

    // Age profile (life_stage)
    if (origin.life_stage && metrics.profile.ageProfiles[origin.life_stage] !== undefined) {
      metrics.profile.ageProfiles[origin.life_stage] += weight;
    }

    // Income profile (affluence)
    if (origin.affluence && metrics.profile.incomeProfiles[origin.affluence] !== undefined) {
      metrics.profile.incomeProfiles[origin.affluence] += weight;
    }

    // Social Grade - use actual population values if available
    const totalPop = origin.tot_pop || 1;
    if (origin.AB_pop !== undefined) {
      metrics.socialGrade['AB'] += weight * (origin.AB_pop / totalPop);
    }
    if (origin.C1_pop !== undefined) {
      metrics.socialGrade['C1'] += weight * (origin.C1_pop / totalPop);
    }
    if (origin.C2_pop !== undefined) {
      metrics.socialGrade['C2'] += weight * (origin.C2_pop / totalPop);
    }
    if (origin.DE_pop !== undefined) {
      metrics.socialGrade['DE'] += weight * (origin.DE_pop / totalPop);
    }

    // Tenure - use actual property values if available
    const totalProperties = origin.tot_properties || 1;
    if (origin.owned_outright !== undefined) {
      metrics.tenure['Owned Outright'] += weight * (origin.owned_outright / totalProperties);
    }
    if (origin.mortgage_shared !== undefined) {
      metrics.tenure['Mortgage/Shared'] += weight * (origin.mortgage_shared / totalProperties);
    }
    if (origin.social_rented !== undefined) {
      metrics.tenure['Social Rented'] += weight * (origin.social_rented / totalProperties);
    }
    if (origin.private_rented !== undefined) {
      metrics.tenure['Private Rented'] += weight * (origin.private_rented / totalProperties);
    }

    // Employment - use actual employment values if available
    const employmentFields = [
      origin.full_time || 0,
      origin.part_time || 0,
      origin.self_employed_with_employees || 0,
      origin.self_employed_solo || 0,
      origin.unemployed_available || 0,
      origin.student_in_employment || 0,
      origin.student_unemployed_available || 0,
      origin.inactive_retired || 0,
      origin.inactive_student || 0,
      origin.inactive_looking_after_home || 0,
      origin.inactive_lts_disabled || 0,
      origin.inactive_other || 0
    ];
    const totalEmployment = employmentFields.reduce((sum, val) => sum + val, 0);

    if (totalEmployment > 0) {
      if (origin.full_time !== undefined) {
        metrics.employment['Full-Time'] += weight * (origin.full_time / totalEmployment);
      }
      if (origin.part_time !== undefined) {
        metrics.employment['Part-Time'] += weight * (origin.part_time / totalEmployment);
      }
      if (origin.self_employed_with_employees !== undefined) {
        metrics.employment['Self-Employed (with employees)'] += weight * (origin.self_employed_with_employees / totalEmployment);
      }
      if (origin.self_employed_solo !== undefined) {
        metrics.employment['Self-Employed (solo)'] += weight * (origin.self_employed_solo / totalEmployment);
      }
      if (origin.unemployed_available !== undefined) {
        metrics.employment['Unemployed'] += weight * (origin.unemployed_available / totalEmployment);
      }
      if (origin.student_in_employment !== undefined || origin.student_unemployed_available !== undefined || origin.inactive_student !== undefined) {
        metrics.employment['Student'] += weight * ((origin.student_in_employment || 0) + (origin.student_unemployed_available || 0) + (origin.inactive_student || 0)) / totalEmployment;
      }
      if (origin.inactive_retired !== undefined) {
        metrics.employment['Retired'] += weight * (origin.inactive_retired / totalEmployment);
      }
      if (origin.inactive_looking_after_home !== undefined) {
        metrics.employment['Home Maker'] += weight * (origin.inactive_looking_after_home / totalEmployment);
      }
      if (origin.inactive_lts_disabled !== undefined) {
        metrics.employment['Long-Term Sick/Disabled'] += weight * (origin.inactive_lts_disabled / totalEmployment);
      }
      if (origin.inactive_other !== undefined) {
        metrics.employment['Other'] += weight * (origin.inactive_other / totalEmployment);
      }
    }

    // Education - use actual education values if available
    const educationFields = [
      origin.no_qualifications || 0,
      origin.level_1 || 0,
      origin.level_2 || 0,
      origin.apprenticeship || 0,
      origin.level_3 || 0,
      origin.level_4 || 0
    ];
    const totalEducation = educationFields.reduce((sum, val) => sum + val, 0);

    if (totalEducation > 0) {
      if (origin.no_qualifications !== undefined) {
        metrics.education['No Qualifications'] += weight * (origin.no_qualifications / totalEducation);
      }
      if (origin.level_1 !== undefined) {
        metrics.education['Level 1'] += weight * (origin.level_1 / totalEducation);
      }
      if (origin.level_2 !== undefined) {
        metrics.education['Level 2'] += weight * (origin.level_2 / totalEducation);
      }
      if (origin.apprenticeship !== undefined) {
        metrics.education['Apprenticeship'] += weight * (origin.apprenticeship / totalEducation);
      }
      if (origin.level_3 !== undefined) {
        metrics.education['Level 3'] += weight * (origin.level_3 / totalEducation);
      }
      if (origin.level_4 !== undefined) {
        metrics.education['Level 4+'] += weight * (origin.level_4 / totalEducation);
      }
    }

    // IMD Decile
    if (origin.imd_decile) {
      const decile = String(origin.imd_decile);
      if (metrics.imd[decile] !== undefined) {
        metrics.imd[decile] += weight;
      }
    }

    // Spend data - accumulate total spend by category
    if (origin.spend_by_pcode_by_category) {
      Object.entries(origin.spend_by_pcode_by_category).forEach(([category, spend]) => {
        if (!metrics.spend.totalSpend[category]) {
          metrics.spend.totalSpend[category] = 0;
        }
        metrics.spend.totalSpend[category] += spend;
      });
    }
  });

  // Calculate average spend per person
  if (totalPopulation > 0) {
    Object.entries(metrics.spend.totalSpend).forEach(([category, totalSpend]) => {
      metrics.spend.spendPerPerson[category] = totalSpend / totalPopulation;
    });
  }

  // Convert to percentages
  Object.keys(metrics.profile.ageProfiles).forEach(key => {
    metrics.profile.ageProfiles[key] = metrics.profile.ageProfiles[key] * 100;
  });
  Object.keys(metrics.profile.incomeProfiles).forEach(key => {
    metrics.profile.incomeProfiles[key] = metrics.profile.incomeProfiles[key] * 100;
  });
  Object.keys(metrics.socialGrade).forEach(key => {
    metrics.socialGrade[key] = metrics.socialGrade[key] * 100;
  });
  Object.keys(metrics.tenure).forEach(key => {
    metrics.tenure[key] = metrics.tenure[key] * 100;
  });
  Object.keys(metrics.employment).forEach(key => {
    metrics.employment[key] = metrics.employment[key] * 100;
  });
  Object.keys(metrics.education).forEach(key => {
    metrics.education[key] = metrics.education[key] * 100;
  });
  Object.keys(metrics.imd).forEach(key => {
    metrics.imd[key] = metrics.imd[key] * 100;
  });

  return metrics;
};

// Calculate similarity percentage between two distributions
export const calculateSimilarity = (dist1, dist2) => {
  if (!dist1 || !dist2) return 0;

  const keys = Object.keys(dist1);
  if (keys.length === 0) return 0;

  // Calculate sum of absolute differences
  let totalDifference = 0;
  keys.forEach(key => {
    const val1 = dist1[key] || 0;
    const val2 = dist2[key] || 0;
    totalDifference += Math.abs(val1 - val2);
  });

  // Similarity = 100% - (average difference / 2)
  // Divided by 2 because differences are counted twice (once for each direction)
  const similarity = 100 - (totalDifference / 2);
  return Math.max(0, Math.min(100, similarity));
};

// Export comparison data as CSV
export const exportToCSV = (activeMetrics, savedMetrics, activeAnalysisName, savedAnalysisName, similarityScores) => {
  const rows = [];

  // Header
  rows.push(['Comparison Report']);
  rows.push([`${activeAnalysisName} vs ${savedAnalysisName}`]);
  rows.push(['']);

  // Overall Similarity Score
  const overallScore = Object.values(similarityScores).reduce((sum, val) => sum + Math.round(val), 0);
  rows.push(['Overall Similarity Score', `${overallScore}/900`]);
  rows.push(['']);

  // Summary Similarity Scores
  rows.push(['Category Similarity Scores']);
  rows.push(['Category', 'Similarity (%)']);
  rows.push(['Age Profile', calculateSimilarity(activeMetrics.profile.ageProfiles, savedMetrics.profile.ageProfiles).toFixed(2)]);
  rows.push(['Income Profile', calculateSimilarity(activeMetrics.profile.incomeProfiles, savedMetrics.profile.incomeProfiles).toFixed(2)]);
  rows.push(['Social Grade', calculateSimilarity(activeMetrics.socialGrade, savedMetrics.socialGrade).toFixed(2)]);
  rows.push(['Tenure', calculateSimilarity(activeMetrics.tenure, savedMetrics.tenure).toFixed(2)]);
  rows.push(['Employment', calculateSimilarity(activeMetrics.employment, savedMetrics.employment).toFixed(2)]);
  rows.push(['Education', calculateSimilarity(activeMetrics.education, savedMetrics.education).toFixed(2)]);
  rows.push(['IMD Decile', calculateSimilarity(activeMetrics.imd, savedMetrics.imd).toFixed(2)]);
  rows.push(['']);

  // Detailed Breakdown
  rows.push(['Detailed Category Breakdown']);
  rows.push(['Metric', 'Category', `${activeAnalysisName} (%)`, `${savedAnalysisName} (%)`, 'Difference (%)']);

  // Age Profiles
  Object.keys(activeMetrics.profile.ageProfiles).forEach(key => {
    const active = activeMetrics.profile.ageProfiles[key];
    const saved = savedMetrics.profile.ageProfiles[key];
    rows.push(['Age Profile', key, active.toFixed(2), saved.toFixed(2), (active - saved).toFixed(2)]);
  });

  rows.push(['']);

  // Income Profiles
  Object.keys(activeMetrics.profile.incomeProfiles).forEach(key => {
    const active = activeMetrics.profile.incomeProfiles[key];
    const saved = savedMetrics.profile.incomeProfiles[key];
    rows.push(['Income Profile', key, active.toFixed(2), saved.toFixed(2), (active - saved).toFixed(2)]);
  });

  rows.push(['']);

  // Social Grade
  Object.keys(activeMetrics.socialGrade).forEach(key => {
    const active = activeMetrics.socialGrade[key];
    const saved = savedMetrics.socialGrade[key];
    rows.push(['Social Grade', key, active.toFixed(2), saved.toFixed(2), (active - saved).toFixed(2)]);
  });

  rows.push(['']);

  // Tenure
  Object.keys(activeMetrics.tenure).forEach(key => {
    const active = activeMetrics.tenure[key];
    const saved = savedMetrics.tenure[key];
    rows.push(['Tenure', key, active.toFixed(2), saved.toFixed(2), (active - saved).toFixed(2)]);
  });

  rows.push(['']);

  // Employment
  Object.keys(activeMetrics.employment).forEach(key => {
    const active = activeMetrics.employment[key];
    const saved = savedMetrics.employment[key];
    rows.push(['Employment', key, active.toFixed(2), saved.toFixed(2), (active - saved).toFixed(2)]);
  });

  rows.push(['']);

  // Education
  Object.keys(activeMetrics.education).forEach(key => {
    const active = activeMetrics.education[key];
    const saved = savedMetrics.education[key];
    rows.push(['Education', key, active.toFixed(2), saved.toFixed(2), (active - saved).toFixed(2)]);
  });

  rows.push(['']);

  // IMD
  Object.keys(activeMetrics.imd).forEach(key => {
    const active = activeMetrics.imd[key];
    const saved = savedMetrics.imd[key];
    rows.push(['IMD Decile', key, active.toFixed(2), saved.toFixed(2), (active - saved).toFixed(2)]);
  });

  // Spend Data (if available)
  if (activeMetrics.spend && savedMetrics.spend &&
      Object.keys(activeMetrics.spend.totalSpend).length > 0 &&
      Object.keys(savedMetrics.spend.totalSpend).length > 0) {
    rows.push(['']);
    rows.push(['Total Spend by Category (£)']);
    rows.push(['Category', activeAnalysisName, savedAnalysisName, 'Difference']);
    Object.keys(activeMetrics.spend.totalSpend).forEach(key => {
      const active = activeMetrics.spend.totalSpend[key] || 0;
      const saved = savedMetrics.spend.totalSpend[key] || 0;
      rows.push([key, active.toFixed(2), saved.toFixed(2), (active - saved).toFixed(2)]);
    });

    rows.push(['']);
    rows.push(['Spend per Person by Category (£)']);
    rows.push(['Category', activeAnalysisName, savedAnalysisName, 'Difference']);
    Object.keys(activeMetrics.spend.spendPerPerson).forEach(key => {
      const active = activeMetrics.spend.spendPerPerson[key] || 0;
      const saved = savedMetrics.spend.spendPerPerson[key] || 0;
      rows.push([key, active.toFixed(2), saved.toFixed(2), (active - saved).toFixed(2)]);
    });
  }

  // Convert to CSV string
  const csvContent = rows.map(row => row.join(',')).join('\n');

  // Create download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  const activeNameSafe = activeAnalysisName.replace(/[^a-z0-9]/gi, '_');
  const savedNameSafe = savedAnalysisName.replace(/[^a-z0-9]/gi, '_');
  link.setAttribute('download', `comparison_${activeNameSafe}_vs_${savedNameSafe}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
